// @ts-ignore
import { toastr } from "react-redux-toastr";
import i18n from "../i18n";

import { put, takeEvery, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import domainSlice, {
  getAlternativeDomainExtensionsFailure,
  getAlternativeDomainExtensionsSuccess,
  searchAlternativeDomainExtensionFailure,
  searchAlternativeDomainExtensionSuccess,
  searchDomainFailure,
  searchDomainSuccess,
  getAvailableDomainsSuccess,
  getAvailableDomainsFailure,
  getNewDomainsSuccess,
  getNewDomainsFailure,
  getResellersSuccess,
  getResellersFailure,
  getWhoisSuccess,
  getWhoisFailure,
  getExpiredDomainsSuccess,
  getExpiredDomainsFailure,
  getDomainDetailsSuccess,
  getDomainDetailsFailure,
} from "reducers/domainSlice";
import HttpResponse from "types/httpResponse";
import SearchResult from "types/searchResult";
import GetAlternativesResponse from "types/getAlternativesResponse";
import GetAvailableDomainsResponse from "types/getAvailableDomainsResponse";
import GetResellersResponse from "types/getResllersResponse";
import GetExpiredDomainsResponse from "types/getExpiredDomainsResponse";
import GetNewDomainsResponse from "types/getNewDomainsResponse";
import DomainDetails from "types/domainDetails";

// Helper to check if we're in pre-render mode
const isPrerendering = () => {
  return (
    typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap"
  );
};

// Wrapper to show error toasts only when not pre-rendering
const showError = (title: string, message: string) => {
  if (!isPrerendering()) {
    toastr.error(title, message);
  }
};

function* searchDomainSaga(action: PayloadAction<string>) {
  try {
    if (action.payload.length === 0) {
      yield put(searchDomainSuccess(undefined));
      return;
    }

    const response: HttpResponse<SearchResult> = yield axios.get(
      `/domains/whois/${encodeURIComponent(action.payload)}`,
    );
    yield put(searchDomainSuccess(response.data));
  } catch (error: any) {
    showError(
      i18n.t("errors.searchingDomain", { ns: "common" }),
      error.message,
    );
    yield put(searchDomainFailure());
  }
}

function* getWhoisSaga(action: PayloadAction<string>) {
  try {
    if (action.payload.length === 0) {
      yield put(searchDomainSuccess(undefined));
      return;
    }

    const response: HttpResponse<SearchResult> = yield axios.get(
      `/domains/whois/${encodeURIComponent(action.payload)}`,
    );
    yield put(getWhoisSuccess(response.data));
  } catch (error: any) {
    yield put(getWhoisFailure(error.message));
  }
}

function* getDomainDetailsSaga(action: PayloadAction<string>) {
  try {
    const response: HttpResponse<DomainDetails> = yield axios.get(
      `/domains/details/${encodeURIComponent(action.payload)}`,
    );
    yield put(getDomainDetailsSuccess(response.data));
  } catch (error: any) {
    yield put(getDomainDetailsFailure(error.message));
  }
}

function* searchAlternativeDomainExtensionSaga(action: PayloadAction<string>) {
  try {
    if (action.payload.length === 0) {
      throw new Error("Empty payload");
    }

    const response: HttpResponse<SearchResult> = yield axios.get(
      `/domains/whois/${encodeURIComponent(action.payload)}`,
    );

    yield put(searchAlternativeDomainExtensionSuccess(response.data));
  } catch (error: any) {
    showError(
      `${i18n.t("errors.searchingAlternativeExtension", { ns: "common" })} ${action.payload}`,
      error.response.status === 400 ? error.response.data : error.message,
    );
    yield put(searchAlternativeDomainExtensionFailure(action.payload));
  }
}

function* getAlternativeDomainExtensionsSaga(action: PayloadAction<string>) {
  try {
    const response: HttpResponse<GetAlternativesResponse> = yield axios.get(
      `/domains/alternativedomainextensions/${encodeURIComponent(
        action.payload,
      )}`,
    );
    yield put(getAlternativeDomainExtensionsSuccess(response.data));
  } catch (error: any) {
    showError(
      i18n.t("errors.alternativeExtensions", { ns: "common" }),
      error.message,
    );
    yield put(getAlternativeDomainExtensionsFailure());
  }
}

function* getExpiredDomainsSaga(
  action: PayloadAction<{ page: number; pageSize?: number }>,
) {
  try {
    const { page, pageSize = 20 } = action.payload;
    const url = `/domains/expired?Page=${page}&PageSize=${pageSize}`;
    const response: HttpResponse<GetExpiredDomainsResponse> =
      yield axios.get(url);
    yield put(getExpiredDomainsSuccess(response.data));
  } catch (error: any) {
    yield put(getExpiredDomainsFailure(error.message));
  }
}

function* getAvailableDomainsSaga(
  action: PayloadAction<{ page: number; pageSize?: number; search?: string }>,
) {
  try {
    const { page, pageSize = 20, search } = action.payload;
    let url = `/domains/available?Page=${page}&PageSize=${pageSize}`;
    if (search) {
      url += `&Search=${encodeURIComponent(search)}`;
    }
    const response: HttpResponse<GetAvailableDomainsResponse> =
      yield axios.get(url);
    yield put(getAvailableDomainsSuccess(response.data));
  } catch (error: any) {
    yield put(getAvailableDomainsFailure(error.message));
  }
}

function* getNewDomainsSaga() {
  try {
    const response: HttpResponse<GetNewDomainsResponse> = yield axios.get(
      "/domains/available/new",
    );
    yield put(getNewDomainsSuccess(response.data));
  } catch (error: any) {
    yield put(getNewDomainsFailure(error.message));
  }
}

function* getResellersSaga() {
  try {
    const response: HttpResponse<GetResellersResponse> =
      yield axios.get("/resellers");
    yield put(getResellersSuccess(response.data));
  } catch (error: any) {
    showError(i18n.t("errors.resellers", { ns: "common" }), error.message);
    yield put(getResellersFailure());
  }
}

function* domainSaga() {
  yield takeLatest(domainSlice.actions.searchDomain, searchDomainSaga);
  yield takeLatest(domainSlice.actions.getWhois, getWhoisSaga);
  yield takeEvery(
    domainSlice.actions.searchAlternativeDomainExtension,
    searchAlternativeDomainExtensionSaga,
  );
  yield takeLatest(
    domainSlice.actions.getAlternativeDomainExtensions,
    getAlternativeDomainExtensionsSaga,
  );
  yield takeLatest(
    domainSlice.actions.getExpiredDomains,
    getExpiredDomainsSaga,
  );
  yield takeLatest(
    domainSlice.actions.getAvailableDomains,
    getAvailableDomainsSaga,
  );
  yield takeLatest(domainSlice.actions.getNewDomains, getNewDomainsSaga);
  yield takeLatest(domainSlice.actions.getDomainDetails, getDomainDetailsSaga);
  yield takeLatest(domainSlice.actions.getResellers, getResellersSaga);
}

export default domainSaga;
